import { useMsal } from '@azure/msal-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import type { IServicesFormData } from '@/components/modals/digital-service/DigitalServiceModal';
import type { ITelemetryService } from '@/service/vpc/types/ITelemetryService';

import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { queryKeys } from '@/service/vpc/queryOptions';
import { getServicesQueryOptions, unitByIdQueryOptions } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';
import { isActivationSuccess } from '@/utils/service-activation';
import { dedupeServicesByName, marketForCountry, retrieveCountry } from '@/utils/services';

// EC_Wifi is a standalone Marine service — always excluded from batch/all-services logic
const EC_WIFI = 'ActiveMonitoringEcWifi' satisfies ITelemetryService['serviceName'];

export type UnitServicesSegment = 'Marine' | 'Industrial';

export interface AllServicesToggleConfig {
  visible: boolean;
  services: ITelemetryService[];
}

export interface UnitServicesResult {
  isLoading: boolean;
  segment: UnitServicesSegment;
  hasServices: boolean;
  services: ITelemetryService[];
  allServicesToggle: AllServicesToggleConfig;
  getDeactivationServices: (service: ITelemetryService) => ITelemetryService[];
  getActivationServices: (service: ITelemetryService) => ITelemetryService[];
}

export const useUnitServices = (): UnitServicesResult => {
  const { endCustomerUnitId } = useParams({ strict: false });
  const { fleetAccountId } = useFleetAccount();
  const { instance } = useMsal();

  const { data: unit, isFetching: isFetchingUnit } = useQuery(
    unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! })
  );
  const { data, isFetching: isFetchingServices } = useQuery(
    getServicesQueryOptions({
      endCustomerUnitId: endCustomerUnitId!,
      fleetAccountId: fleetAccountId!,
    })
  );

  const segment: UnitServicesSegment = unit?.mainSegment ?? 'Industrial';
  const isLoading = isFetchingUnit || isFetchingServices;

  // Collapse market-clone rows to one per serviceName (bug 1539968) before anything
  // downstream (row list, allServicesToggle, activation bundles) consumes them.
  const userMarket = marketForCountry(retrieveCountry(instance.getActiveAccount()));

  // ActiveMonitoringCore must sort first so Marine TGW dependency order is clear in the UI
  const services = dedupeServicesByName(data?.services[endCustomerUnitId!] ?? [], userMarket).sort((a, b) =>
    a.serviceName === 'ActiveMonitoringCore' ? -1 : b.serviceName === 'ActiveMonitoringCore' ? 1 : 0
  );

  const hasServices = services.length > 0;

  // EC_Wifi is excluded from batch deactivation tracking — it is always standalone
  const activeNonEcWifiServices = services.filter((s) => s.status === 'Active' && s.serviceName !== EC_WIFI);

  // "Activate all services" header button (Industrial only — Marine cards each bundle
  // their own activation): shown in all states, and activates whatever subset of
  // services is currently activatable. The backend flags already exclude Active/
  // Requested rows and rows whose required dependency is in-flight; the button
  // renders disabled when the subset is empty.
  // SAFETY: product decision (AB#1602995) — while any service is terminating the
  // button stays disabled, since activating alongside a pending deactivation misbehaved.
  const hasTerminatingService = services.some((s) => s.status === 'Deactivating requested');
  const allServicesToggleServices = hasTerminatingService
    ? []
    : services.filter(
        (s) =>
          s.supported !== false && s.serviceName !== EC_WIFI && (s.canActivate || s.dependencyInfo.canBatchActivate)
      );

  const allServicesToggleVisible = segment === 'Industrial';

  // Deactivation cascade: when deactivating ActiveMonitoring (Industrial) or
  // ActiveMonitoringCore (Marine), all other active non-EcWifi services must be
  // deactivated together because they depend on it. Every other service terminates
  // alone (AB#1584415) — dependents never take their parent down with them, and the
  // backend's deactivate cascade only walks parent -> dependent for the same reason.
  const getDeactivationServices = (service: ITelemetryService): ITelemetryService[] => {
    const isAnchorService =
      (segment === 'Industrial' && service.serviceName === 'ActiveMonitoring') ||
      (segment === 'Marine' && service.serviceName === 'ActiveMonitoringCore');

    if (isAnchorService && activeNonEcWifiServices.length > 1) {
      return activeNonEcWifiServices;
    }

    return [service];
  };

  // Activation bundle: on Marine TGW, activating ActiveMonitoring (CSE) requires
  // ActiveMonitoringCore in the same submission because CSE depends on Core. The
  // returned array drives the per-row ServiceItem so the dealer modal opens with
  // both services and the batch path is taken on submit.
  const coreService = services.find((s) => s.serviceName === 'ActiveMonitoringCore');

  const getActivationServices = (service: ITelemetryService): ITelemetryService[] => {
    const needsCoreBundling =
      segment === 'Marine' &&
      service.serviceName === 'ActiveMonitoring' &&
      coreService !== undefined &&
      coreService.status !== 'Active';

    return needsCoreBundling ? [coreService!, service] : [service];
  };

  return {
    isLoading,
    segment,
    hasServices,
    services,
    allServicesToggle: {
      visible: allServicesToggleVisible,
      services: allServicesToggleServices,
    },
    getDeactivationServices,
    getActivationServices,
  };
};

// Segment-aware activation & deactivation:
//   Marine      → POST /issb/services/activate   | /issb/services/deactivate
//   Industrial  → POST /issb/services/batch-activate or /active-monitoring/request-activate
//                 POST /issb/services/batch-deactivate
export const useServiceProvisioning = (segment: UnitServicesSegment) => {
  const queryClient = useQueryClient();
  const { fleetAccountId } = useFleetAccount();

  const invalidateServiceQueries = () => {
    queryClient.invalidateQueries({ queryKey: [queryKeys.getServices], exact: false });
    queryClient.invalidateQueries({ queryKey: [queryKeys.getUnitsWithFBB] });
    queryClient.invalidateQueries({ queryKey: [queryKeys.getUnitsLastSeen] });
  };

  // Activation mutations
  const activateMarineMutation = useMutation({
    mutationFn: vpcService.Services.activateMarineServices,
  });
  const activateBatchMutation = useMutation({
    mutationFn: vpcService.Services.activateBatchServicess,
  });
  const activateSingleMutation = useMutation({
    mutationFn: vpcService.Services.activateService,
  });

  // Deactivation mutations
  const deactivateMarineMutation = useMutation({
    mutationFn: vpcService.Services.deactivateMarineService,
    onSettled: invalidateServiceQueries,
  });
  const deactivateBatchMutation = useMutation({
    mutationFn: vpcService.Services.deactivateBatchServices,
    onSettled: invalidateServiceQueries,
  });

  const activateServices = async (
    endCustomerUnitId: string,
    serviceIds: number[],
    formData: IServicesFormData
  ): Promise<boolean> => {
    if (!fleetAccountId) return false;

    if (segment === 'Marine') {
      const response = await activateMarineMutation.mutateAsync({
        endCustomerUnitId,
        serviceIds,
        fleetAccountId,
        formData,
      });
      return isActivationSuccess(response.errors);
    }
    if (serviceIds.length > 1) {
      const response = await activateBatchMutation.mutateAsync({
        endCustomerUnitId,
        serviceIds,
        fleetAccountId,
        formData,
      });
      return isActivationSuccess(response.errors);
    }
    const response = await activateSingleMutation.mutateAsync({
      endCustomerUnitId,
      serviceId: serviceIds[0],
      fleetAccountId,
      formData,
    });
    return isActivationSuccess(response.errors);
  };

  const deactivateServices = async (endCustomerUnitId: string, services: ITelemetryService[]) => {
    if (!fleetAccountId) return;

    if (segment === 'Marine') {
      await Promise.all(
        services
          .filter((s) => s.individualServiceId != null)
          .map((service) =>
            deactivateMarineMutation.mutateAsync({
              endCustomerUnitId,
              individualServiceId: service.individualServiceId!,
              fleetAccountId,
            })
          )
      );
    } else {
      await deactivateBatchMutation.mutateAsync({
        endCustomerUnitId,
        serviceIds: services.map((s) => s.serviceId),
        fleetAccountId,
      });
    }
  };

  return {
    activateServices,
    deactivateServices,
    isPending:
      activateMarineMutation.isPending ||
      activateBatchMutation.isPending ||
      activateSingleMutation.isPending ||
      deactivateMarineMutation.isPending ||
      deactivateBatchMutation.isPending,
  };
};
